'use client';

import { createClient } from '@/lib/supabase/client';
import { useEffect, useState } from 'react';
import { Notification } from '@/lib/db/schema';
import { toast } from '@ui/components/ui/sonner';
import { useRouter } from 'next/navigation';
import { getNotifications, markAsRead, markAllAsRead, deleteAllNotifications } from '@/dal/notifications/actions';
import { parseDate } from '@ui/lib/date-utils';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useTranslations } from 'next-intl';

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[] | undefined>();
  const t = useTranslations('Notifications');
  const router = useRouter();

  useEffect(() => {
    const supabase = createClient();

    // Fetch initial notifications
    getNotifications().then((data) => {
      setNotifications(data);
    });

    if (process.env.NODE_ENV === 'development') return;

    let subscription: RealtimeChannel | undefined = undefined;
    supabase.auth.getSession().then(({ data: { session } }) => {
      supabase.realtime.setAuth(session?.access_token || null);

      subscription = supabase
        .channel('notifications')
        .on<Partial<Notification>>('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, (payload) => {
          if (payload.eventType === 'DELETE') {
            setNotifications((prev) => prev?.filter((n) => n.id !== payload.old?.id));
            return;
          }

          // Parse snake case to camel case
          const newNotification = Object.fromEntries(
            Object.entries(payload.new).map(([key, value]) => {
              const camelKey = key.replace(/_([a-z])/g, (g) => g[1]!.toUpperCase());
              if (key === 'created_at') value = parseDate(value as string);
              return [camelKey, value];
            })
          ) as Notification;

          setNotifications((prev) =>
            [newNotification, ...(prev ? prev.filter((notification) => notification.id !== newNotification.id) : [])].sort(
              (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
            )
          );

          if (payload.eventType === 'INSERT' && !newNotification.isRead) showNotificationToast(newNotification, router, t);
        })
        .subscribe((status, error) => {
          if (error) console.error('Subscription error:', error, status);
        });
    });

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    notifications,
    setNotifications,
    markAsRead,
    markAllAsRead,
    deleteAllNotifications,
  };
}

function showNotificationToast(notification: Notification, router: ReturnType<typeof useRouter>, t: (key: string) => string) {
  const currentPath = process.env.NEXT_PUBLIC_SITE_URL + window.location.pathname;

  if (notification.url && currentPath === notification.url) {
    markAsRead(notification.id);
    return;
  }

  const action = notification.url
    ? {
        label: t('view'),
        onClick: () => {
          router.push(notification.url!);
          markAsRead(notification.id);
        },
      }
    : undefined;

  switch (notification.type) {
    case 'workflow_execution_completed':
      toast.success(notification.message, { action });
      break;
    case 'workflow_execution_failed':
      toast.error(notification.message);
      break;
    case 'workflow_execution_404_error_code':
      toast.warning(notification.message, { action });
      break;
    default:
      toast(notification.message, { action });
      break;
  }
}
